import React, { useEffect, useState } from "react";
import { Person, rng, stringUtil } from "../../../amorg-common";
import { createPerson, getPersons } from "../../../api/personClientApi";
import { H2 } from "../../../designSystem/typography/Heading";
import { PersonEditor } from "./PersonEditor";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Modal } from "../../../designSystem/Modal";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Button } from "../../../designSystem/button/Button";
import { ListItem } from "../../../designSystem/List/ListItem";
import { Icon, iconName } from "../../../designSystem/icons/Icons";
import styled from "styled-components";


type State = {
    persons:Person[]
    inEdit:Person | null
}

export default function PersonList():JSX.Element
{
    const [loading, setLoading] = useState(true)
    const [state, setState] = React.useState<State>({
        persons:[],
        inEdit:null
    })

    async function getSortedPersons()
    {
        const persons = await getPersons();
        return persons.sort((a,b) => a.name.localeCompare(b.name));
    }

    useEffect(() => {
        const getData = async () => {
            await setState({
                persons: await getSortedPersons(),
                inEdit:null
            });
            setLoading(false);
        };
        getData();
    }, [])

    async function handleUpdated()
    {
        setLoading(true);
        const persons = await getSortedPersons();
        const inEditPerson = state.inEdit ? persons.find(x => x.id === state.inEdit?.id) : null;
        setState({
            persons: persons,
            inEdit: inEditPerson || null
        });
        setLoading(false);
    }

    async function handleCreatePerson(){
        
        setLoading(true);
        const newPersonId = rng.getRandomId();
        const nameBase = 'Ny ansvarlig';
        const nextDescription = stringUtil.generateNextSequenceValue(nameBase, state.persons.map(fa => fa.name));
        const result = await createPerson(newPersonId, "FacilityOwner", nextDescription, "", "", "" );

        if( result.isSuccess){
            const persons = await getSortedPersons();
            const newPerson = persons.find(x => x.id === newPersonId);
            setState({
                persons,
                inEdit:newPerson || null
            });
        }
        setLoading(false);
    }

    function handleClickPerson(personId:string)
    {
        setState({ ...state, inEdit:state.persons.find(x => x.id === personId) || null });
    }

    async function handleDeleted()
    { 
        setState({
            persons:await getSortedPersons(),
            inEdit:null
        });
        setLoading(false);
    }

    async function handleClosed(){
        setState({
            ...state,
            inEdit:null
        });
    }

    return (
        <>
        <LoadingPageOverlay loading={loading} />

            <>
            <Flex flexDirection="column" gap={16}>

                <H2>
                    Facilitetsansvarlige
                </H2>

                {state.persons.map(person => {
                    const secondaryItems:JSX.Element[] = [];
                    if(person.role)
                        secondaryItems.push(<SecondaryItem icon="interface_user_user" content={person.role} />);
                    if(person.email)
                    {
                        secondaryItems.push(<SecondaryItem icon="interface_glyph_email" content={person.email} />);
                    }
                    if(person.phone)
                    {
                        secondaryItems.push(<SecondaryItem icon="interface_glyph_phone" content={person.phone} />);
                    }

                    return (
                        <ListItem key={person.id} border
                            leadingIcon="interface_user_user"
                            primaryContent={person.name}
                            secondary={{
                                content: secondaryItems.length > 0
                                    ?   <SecondaryItemWrapper>
                                            {secondaryItems.map(si => si)}
                                        </SecondaryItemWrapper>
                                    : null
                            }}
                            buttons={[
                                { type:'button', icon:"interface_actions_edit", onClick:() => handleClickPerson(person.id) }
                            ]}
                        />
                    );
                })}
            </Flex>

            <Button variant="text"
                onClick={handleCreatePerson}
                icon="actions_plus"
                text="Opret ny ansvarlig" />

            <Modal 
                showModal={state?.inEdit !== null}
                title={"Rediger ansvarlig"}
                content={
                    <PersonEditor
                        person={state.inEdit as Person}
                        onDeleted={handleDeleted}
                        onUpdated={handleUpdated}
                        onClose={handleClosed}
                    />
                }
                onCancel={handleClosed} />
            </>
        </>
    );
}

const SecondaryItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

function SecondaryItem({ icon, content }: { icon: iconName, content: string }) {
    return (
        <Flex gap={4} alignItems="center">
            <Icon name={icon} size="x-small"/>
            <span>{content}</span>
        </Flex>
    );
}


